/**
 * Pieces the PF-4 ticket screens share: the reverted-to-draft notice, the
 * money cell, and the rate-card option text.
 *
 * NOTHING HERE ADDS UP. A line's amount, the draft subtotal, the tax and the
 * total are the API's figures; an absent one renders `—`.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { fmtCurrency } from '../../../lib/display-labels';
import { pfRateUnitSuffix, PF_REVERTED_NOTICE } from '../../../lib/pf-tickets';
import type { PfRate } from '../../../types/pf-tickets';

/**
 * Said when a write comes back `reverted_to_draft: true`. A warning, not a
 * success and not a refusal: amber edge, role="status", stays until dismissed.
 */
export function PfRevertedNotice({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div
      role="status"
      data-testid="pf-ticket-reverted"
      className="flex items-start gap-3 rounded-fi-control border border-fi-amber/60 bg-fi-signal-amber/10 px-4 py-3 text-sm text-fieldiq-text"
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-fi-signal-amber" aria-hidden="true" />
      <p className="flex-1">{PF_REVERTED_NOTICE}</p>
      <button type="button" className="text-xs font-bold underline" onClick={onDismiss}>
        Dismiss
      </button>
    </div>
  );
}

/** A rate as `CA$450.00/hr`; an absent rate is `—`. */
export function pfRateText(rate: number | null | undefined, currency: string | null | undefined, unit: string | null | undefined, distanceUnit?: string | null) {
  const v = fmtCurrency(rate, currency);
  return v === '—' ? v : `${v}${pfRateUnitSuffix(unit, distanceUnit)}`;
}

/** The rate-card option text: `LAB-OP · Operator — CA$95.00/hr (Default card)`. */
export function pfRateOption(r: PfRate) {
  const who = r.customerId ? r.customerName || 'Customer card' : 'Default card';
  return `${r.code} · ${r.name} — ${pfRateText(r.rate, r.currency, r.unit, r.distanceUnit)} (${who})`;
}

/**
 * The rates a ticket's line may be priced from: this ticket's customer or the
 * default card, in the ticket's currency, live on the day (when known). The API
 * checks all of it again (`rate_other_customer`, `rate_other_currency`).
 */
export function ratesForTicket(
  rates: PfRate[],
  t: { customerId: string | null; currency: string | null },
  opts: { unit?: string | null; kind?: string | null; on?: string | null } = {},
) {
  return rates.filter(
    (r) =>
      (r.customerId === null || r.customerId === t.customerId) &&
      (!t.currency || !r.currency || r.currency === t.currency) &&
      (!opts.unit || r.unit === opts.unit) &&
      (!opts.kind || r.kind === opts.kind) &&
      (!opts.on || (r.effectiveFrom <= opts.on && (!r.effectiveTo || r.effectiveTo >= opts.on))),
  );
}
